<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3
                class="font-weight-bold"
                v-text="
                  isDeletePage() != true
                    ? 'Prescription Items'
                    : 'Deleted Prescription Items'
                "
              ></h3>
              <section v-if="isDeletePage() != true">
                <v-btn
                  class="mx-2 mb-2"
                  fab
                  x-small
                  color="primary"
                  to="/prescription-items/create"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </section>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="searchKeyword"
                    label="Search prescription items..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(searchKeyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="activeFilter"
                    :items="activeItems"
                    label="Active Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="prescriptionFilter"
                    :items="prescriptionFilterItems"
                    :item-text="getPrescriptionDisplayText"
                    item-value="id"
                    label="Prescription"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-file-document"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="productFilter"
                    :items="productFilterItems"
                    :item-text="getProductDisplayText"
                    item-value="id"
                    label="Product"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-pill"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title v-if="prescriptionItems.length != 0">
              <v-spacer></v-spacer>
              <v-btn
                rounded
                class="primary mx-4"
                @click="exportToExcel"
                v-if="isDeletePage() != true"
              >
                <span v-if="isExporting">Generating Excel...</span>
                <span v-else>Generate Excel</span>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="285"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Prescription</th>
                      <th class="text-left">Product</th>
                      <th class="text-left">Dosage</th>
                      <th class="text-left">Frequency</th>
                      <th class="text-left">Duration</th>
                      <th class="text-left">Mode</th>
                      <th class="text-left">Notes</th>
                      <th class="text-left">Active</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="prescriptionItems.length != 0">
                    <tr
                      v-for="(item, i) in prescriptionItems"
                      :key="`prescription-item${i}`"
                    >
                      <td style="min-width: 85px">
                        {{ i + 1 }}
                      </td>
                      <td>
                        <div v-if="item.prescription">
                          <v-chip small outlined color="primary">
                            {{ `Prescription #${item.prescription.id}` }}
                          </v-chip>
                          <br />
                          <small
                            class="text--secondary"
                            v-if="item.prescription.doctor"
                          >
                            Dr. {{ item.prescription.doctor.user_name }}
                          </small>
                        </div>
                        <span v-else class="text--secondary"
                          >No Prescription</span
                        >
                      </td>
                      <td>
                        <div v-if="item.product">
                          <v-chip small outlined color="info">
                            {{ item.product.name }}
                          </v-chip>
                          <br />
                          <small
                            class="text--secondary"
                            v-if="item.product.category"
                          >
                            {{ item.product.category }}
                          </small>
                        </div>
                        <span v-else class="text--secondary">No Product</span>
                      </td>
                      <td>{{ formatDosage(item.dosage) }}</td>
                      <td>{{ item.frequency || "Not specified" }}</td>
                      <td>{{ item.duration || "Not specified" }}</td>
                      <td>
                        {{
                          item.mode ? item.mode.description : "Not specified"
                        }}
                      </td>
                      <td>{{ truncateText(item.notes, 50) || "No notes" }}</td>
                      <td>
                        <v-tooltip bottom v-if="item.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Prescription Item is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="item.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Prescription Item is Inactive</span>
                        </v-tooltip>
                      </td>
                      <td class="text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="openDeleteDialog(item)"
                              >
                                <v-icon dark>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this prescription item</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="ml-2"
                                fab
                                x-small
                                color="primary"
                                :to="`/prescription-items/${encryptIt(
                                  item.id
                                )}`"
                              >
                                <v-icon dark>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this prescription item</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="success"
                                @click="restorePrescriptionItem(item)"
                              >
                                <v-icon dark>mdi-delete-restore</v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this prescription item</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="10" class="text-center">
                        No prescription items found
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />

      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_prescription_item" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Prescription Item restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_prescription_item = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "PrescriptionItemsIndex",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      prescriptionItems: [],
      isLoading: false,
      isExporting: false,
      isDeleting: false,
      searchKeyword: "",
      activeFilter: "",
      showDeleted: false,
      prescriptionFilter: null,
      productFilter: null,
      prescriptionFilterItems: [],
      productFilterItems: [],
      totalItems: 0,
      is_deleted_prescription_item: false,
      is_restored_prescription_item: false,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      page: 1,
      rowsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,

      // Filter options
      activeItems: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
    };
  },

  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    // Pre-fill prescription filter from URL query (e.g. from Prescriptions list)
    if (this.$route.query.prescription_id) {
      this.prescriptionFilter = this.$route.query.prescription_id;
    }
    this.getData();
    this.getMasters();
  },

  methods: {
    async getData() {
      console.log("Get Prescription Items Data");
      this.showDeleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.searchKeyword = this.searchKeyword || "";
      this.activeFilter = this.activeFilter || "";
      this.prescriptionFilter = this.prescriptionFilter || "";
      this.productFilter = this.productFilter || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.showDeleted +
        "&is_active=" +
        this.activeFilter +
        "&prescription_id=" +
        this.prescriptionFilter +
        "&product_id=" +
        this.productFilter +
        "&search_keyword=" +
        this.searchKeyword;

      await axios
        .get(`prescription_items?${query}`)
        .then((response) => {
          this.prescriptionItems = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescription_items");

        if (response.data?.prescriptions) {
          this.prescriptionFilterItems = response.data.prescriptions;
        }

        if (response.data?.products) {
          this.productFilterItems = response.data.products;
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    getPrescriptionDisplayText(prescription) {
      if (prescription.doctor) {
        return `Prescription #${prescription.id} - Dr. ${prescription.doctor.user_name}`;
      }
      return `Prescription #${prescription.id}`;
    },

    getProductDisplayText(product) {
      if (product.category) {
        return `${product.name} (${product.category})`;
      }
      return product.name;
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatDosage(dosage) {
      if (!dosage) return "Not specified";
      return `${dosage}mg`;
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/prescription_items/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },

    async deletePrescriptionItem(item) {
      await axios
        .post(`/prescription_items/delete/${this.encryptIt(item.id)}`)
        .then(() => {
          this.is_deleted_prescription_item = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async restorePrescriptionItem(item) {
      await axios
        .post(`/prescription_items/restore/${this.encryptIt(item.id)}`)
        .then(() => {
          this.is_restored_prescription_item = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async exportToExcel() {
      this.isExporting = true;

      let query =
        "show_deleted=" +
        this.showDeleted +
        "&is_active=" +
        this.activeFilter +
        "&prescription_id=" +
        this.prescriptionFilter +
        "&product_id=" +
        this.productFilter +
        "&search_keyword=" +
        this.searchKeyword;

      try {
        const response = await axios.get(`prescription_items/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `prescription_items_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }

      this.isExporting = false;
    },
  },
};
</script>

<style scoped>
.striped-table tbody tr:nth-child(odd) {
  background-color: #f5f5f5;
}

.action_th {
  width: 120px;
  min-width: 120px;
}
</style>